import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { projectsAPI, glAccountsAPI } from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import { Plus, Edit2, Trash2, Upload, Download, ArrowLeft } from 'lucide-react';

export default function ProjectLines() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectLines, setProjectLines] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    nominalCode: '',
    description: '',
    budgetedAmount: ''
  });

  const canManage = hasPermission('manage_finance_settings');

  useEffect(() => {
    fetchProjects();
    fetchGlAccounts();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectLines(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getAll();
      const projectsData = response.data?.projects || (Array.isArray(response.data) ? response.data : []);
      const availableProjects = projectsData.filter(
        p => p.Status === 'Active' || p.Status === 'OnHold'
      );
      setProjects(availableProjects);
      
      // Check if projectId is in URL params
      const projectIdFromUrl = searchParams.get('projectId');
      if (projectIdFromUrl && availableProjects.some(p => p.ProjectID === projectIdFromUrl)) {
        setSelectedProject(projectIdFromUrl);
      } else if (availableProjects.length > 0) {
        setSelectedProject(availableProjects[0].ProjectID);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectLines = async (projectId) => {
    try {
      const response = await projectsAPI.getById(projectId);
      setProjectLines(response.data.lines || []);
    } catch (err) {
      console.error('Error fetching project lines:', err);
    }
  };

  const fetchGlAccounts = async () => {
    try {
      const response = await glAccountsAPI.getAll();
      setGlAccounts(response.data?.glAccounts || response.data?.items || response.data || []);
    } catch (err) {
      console.error('Error fetching GL accounts:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;

    try {
      if (editingLine) {
        await projectsAPI.updateLine(selectedProject, editingLine.ProjectLineID, formData);
      } else {
        await projectsAPI.createLine(selectedProject, formData);
      }
      setShowModal(false);
      resetForm();
      fetchProjectLines(selectedProject);
    } catch (err) {
      console.error('Error saving project line:', err);
      alert(err.response?.data?.message || 'Failed to save project line');
    }
  };

  const handleDelete = async (lineId) => {
    if (!window.confirm('Are you sure you want to delete this project line?')) return;
    
    try {
      await projectsAPI.deleteLine(selectedProject, lineId);
      fetchProjectLines(selectedProject);
    } catch (err) {
      console.error('Error deleting project line:', err);
      alert('Failed to delete project line');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setEditingLine(null);
    setShowModal(true);
  };

  const openEditModal = (line) => {
    setEditingLine(line);
    setFormData({
      nominalCode: line.NominalCode || '',
      description: line.Description || '',
      budgetedAmount: line.BudgetedAmount || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      nominalCode: '',
      description: '',
      budgetedAmount: ''
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!selectedProject) {
      alert('Please select a project first');
      return;
    }

    // Validate file type
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      alert('Please upload an Excel (.xlsx, .xls) or CSV file');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await projectsAPI.importLines(selectedProject, formData);
      
      alert(`Successfully imported ${response.data.count} project lines`);
      fetchProjectLines(selectedProject);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error importing project lines:', err);
      const errorMessage = err.response?.data?.message || 'Failed to import project lines';
      const errors = err.response?.data?.errors;
      
      if (errors && errors.length > 0) {
        alert(`${errorMessage}\n\nErrors:\n${errors.join('\n')}`);
      } else {
        alert(errorMessage);
      }
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    // Create sample data
    const template = [
      ['Nominal Code', 'Description', 'Budgeted Amount'],
      ['4000', 'Materials & Supplies', '10000.00'],
      ['4100', 'Labor Costs', '25000.00'],
      ['4200', 'Equipment Rental', '5000.00']
    ];

    // Convert to CSV
    const csv = template.map(row => row.join(',')).join('\n');
    
    // Create download link
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'project_lines_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const currentProject = projects.find(p => p.ProjectID === selectedProject);
  const totalBudgeted = projectLines.reduce((sum, line) => sum + (line.BudgetedAmount || 0), 0);
  const totalSpent = projectLines.reduce((sum, line) => sum + (line.SpentAmount || 0), 0);
  const totalRemaining = projectLines.reduce((sum, line) => sum + (line.RemainingAmount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Back Button */}
      {searchParams.get('projectId') && (
        <button
          onClick={() => navigate('/projects')}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Projects
        </button>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Project Lines</h1>
          <p className="text-gray-600 mt-1">Manage GL account allocations for projects</p>
        </div>
        {canManage && selectedProject && (
          <div className="flex gap-2">
            <button 
              onClick={downloadTemplate} 
              className="btn-secondary flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download Template
            </button>
            <button 
              onClick={() => fileInputRef.current?.click()} 
              className="btn-secondary flex items-center gap-2"
              disabled={importing}
            >
              <Upload className="w-4 h-4" />
              {importing ? 'Importing...' : 'Import Lines'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button onClick={openCreateModal} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add Line
            </button>
          </div>
        )}
      </div>

      {/* Project Selector - Only show if not coming from URL parameter */}
      {!searchParams.get('projectId') && (
        <div className="card">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Project
          </label>
          <select
            value={selectedProject || ''}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select a project...</option>
            {projects.map(project => (
              <option key={project.ProjectID} value={project.ProjectID}>
                {project.ProjectCode} - {project.ProjectName} ({project.Status})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Show selected project info when coming from URL */}
      {searchParams.get('projectId') && currentProject && (
        <div className="card bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Viewing Lines For:</p>
              <p className="text-lg font-bold text-gray-900 mt-1">
                {currentProject.ProjectCode} - {currentProject.ProjectName}
              </p>
              {currentProject.DepartmentName && (
                <p className="text-sm text-gray-600 mt-1">
                  Department: {currentProject.DepartmentName}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {selectedProject && currentProject && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card">
            <p className="text-sm text-gray-600">Total Budget</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              £{(currentProject.Budget || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="card">
            <p className="text-sm text-gray-600">Allocated</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">
              £{totalBudgeted.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="card">
            <p className="text-sm text-gray-600">Spent</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">
              £{totalSpent.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="card">
            <p className="text-sm text-gray-600">Remaining</p>
            <p className={`text-2xl font-bold mt-1 ${totalRemaining < 0 ? 'text-red-600' : 'text-green-600'}`}>
              £{totalRemaining.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      )}

      {/* Project Lines Table */}
      {selectedProject && (
        <div className="card">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nominal Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Budgeted</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Spent</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Committed</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Remaining</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Utilization</th>
                    {canManage && (
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {projectLines.length === 0 ? (
                    <tr>
                      <td colSpan={canManage ? 8 : 7} className="px-4 py-12 text-center text-gray-500">
                        No project lines found. Add a line to get started.
                      </td>
                    </tr>
                  ) : (
                    projectLines.map((line) => {
                      const utilizationPercent = line.BudgetedAmount > 0 
                        ? ((line.SpentAmount + line.CommittedAmount) / line.BudgetedAmount) * 100 
                        : 0;
                      
                      return (
                        <tr key={line.ProjectLineID} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{line.NominalCode}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{line.Description || '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900">
                            £{(line.BudgetedAmount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-orange-600">
                            £{(line.SpentAmount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-yellow-600">
                            £{(line.CommittedAmount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                          </td>
                          <td className={`px-4 py-3 text-sm text-right font-medium ${
                            (line.RemainingAmount || 0) < 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            £{(line.RemainingAmount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                                <div
                                  className={`h-2 rounded-full ${
                                    utilizationPercent > 100 ? 'bg-red-500' :
                                    utilizationPercent > 80 ? 'bg-yellow-500' :
                                    'bg-green-500'
                                  }`}
                                  style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
                                />
                              </div>
                              <span className={`text-xs font-medium ${
                                utilizationPercent > 100 ? 'text-red-600' :
                                utilizationPercent > 80 ? 'text-yellow-600' :
                                'text-green-600'
                              }`}>
                                {utilizationPercent.toFixed(0)}%
                              </span>
                            </div>
                          </td>
                          {canManage && (
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => openEditModal(line)}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(line.ProjectLineID)}
                                  className="text-red-600 hover:text-red-800"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingLine ? 'Edit Project Line' : 'Add Project Line'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nominal Code *
                </label>
                <select
                  value={formData.nominalCode}
                  onChange={(e) => setFormData({ ...formData, nominalCode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select GL Account...</option>
                  {glAccounts.map(account => (
                    <option key={account.AccountCode} value={account.AccountCode}>
                      {account.AccountCode} - {account.AccountName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budgeted Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.budgetedAmount}
                  onChange={(e) => setFormData({ ...formData, budgetedAmount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="0.00"
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingLine ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
